import { Icon } from '@iconify/react';
import { useFieldArray, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import { AiParameterItem } from '@/Domain/components/Action/HttpRequestActionSheet/AIParameter/AiParameterItem';
import { Button, IconButton } from '@/Domain/components/Shared/Button/Button';
import { Separator } from '@/Domain/components/Shared/Separator/Separator';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/Domain/components/Shared/Tooltip/Tooltip';
import { ParameterTypeEnum } from '@/Domain/enum/ParameterType.enum';
import {
  HttpActionForm,
  PARAMETER_LIMIT,
} from '@/Domain/hooks/Action/ApiAction/useHttpRequestActionFormV2.hook';

type Props = {
  form: UseFormReturn<HttpActionForm>;
};

export const AiParameters = ({ form }: Props) => {
  const { t } = useTranslation('actions_page');

  const { fields, remove, append } = useFieldArray({
    control: form.control,
    name: 'parameters',
    keyName: 'fieldId',
  });

  const parameterLimitLeft = PARAMETER_LIMIT - fields.length;

  const handleAddEmptyField = () =>
    append({
      id: uuidv4(),
      name: '',
      description: '',
      default_value: '',
      is_required: true,
      type: ParameterTypeEnum.String,
    });

  if (!fields.length) {
    return (
      <Button onClick={handleAddEmptyField}>
        <Icon icon="mingcute:add-line" />
        {t('sheet.button.add_item')}
      </Button>
    );
  }

  return (
    <TooltipProvider>
      <div>
        <section className="grid h-16 grid-cols-[175px_minmax(100px,0.5fr)_minmax(125px,1fr)_minmax(100px,0.5fr)_minmax(60px,0.2fr)_auto_auto] items-center gap-4 border-b border-border bg-accent px-4 text-sm font-medium text-muted-foreground">
          <span>{t('sheet.form.data_to_collect.table.format')}</span>
          <span>{t('sheet.form.data_to_collect.table.name')}</span>
          <span>{t('sheet.form.data_to_collect.table.description')}</span>
          <span>{t('sheet.form.data_to_collect.table.default_value')}</span>
          <span>Required</span>
          <span />
          <Tooltip>
            <TooltipTrigger asChild>
              <span>
                <IconButton
                  size="sm"
                  onClick={handleAddEmptyField}
                  disabled={parameterLimitLeft === 0}
                >
                  <Icon icon="mingcute:add-line" />
                </IconButton>
              </span>
            </TooltipTrigger>
            <TooltipContent align="end">
              {parameterLimitLeft === 0 ? t('tooltip.parameter_limit_max') : t('tooltip.add')}
            </TooltipContent>
          </Tooltip>
        </section>

        {fields.map((field, index) => (
          <div key={field.id}>
            <AiParameterItem form={form} index={index} remove={remove} />
            <Separator />
          </div>
        ))}
      </div>
    </TooltipProvider>
  );
};
